import React, { useEffect, useState } from 'react';
import imageCompression from 'browser-image-compression';
import { Button, Form, Upload } from 'antd';
import { RcFile, UploadFile, UploadFileStatus } from 'antd/es/upload/interface';
import { DeleteOutlined, LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { ResData } from '@/@types';
import axiosRequest from '@/api';
import { ImgUploadData } from '@/@types/ImgUpload';

interface ImageUploadComponentProps {
  onImageUpload: (imageUrl: string[]) => void;
  initialImageUrls?: string[];
  bannerType: string;
}

interface ImagePreviewProps {
  imagePreview: string;
  onRemove: () => void;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ imagePreview, onRemove }) => {
  return (
    <div
      style={{
        position: 'relative',
        width: '100px', // 고정 너비 설정
        height: '100px', // 고정 높이 설정
        overflow: 'hidden', // 오버플로우 방지
        marginTop: '10px',
        marginBottom: '10px',
      }}
    >
      <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      <Button
        icon={<DeleteOutlined />}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
        }}
        className="delete-button"
        onClick={onRemove}
      />
    </div>
  );
};

const ImageUpload: React.FC<ImageUploadComponentProps & { initialImageUrls?: string[] }> = ({
  onImageUpload,
  initialImageUrls = [],
  bannerType,
}) => {
  const [imagePreviews, setImagePreviews] = useState<string[]>(initialImageUrls || []);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);

  const uploadText =
    bannerType === 'banner_img' ? (
      <p className="ant-upload-text">
        이미지를 드래그하거나 클릭하여 업로드하세요
        <br />
        (800px X 800px 크기)
      </p>
    ) : (
      <p className="ant-upload-text">
        가로 800px 세로 420px의
        <br />
        크기로 업로드하세요
      </p>
    );

  useEffect(() => {
    if (initialImageUrls.length > 0) {
      const initialFileList: UploadFile[] = initialImageUrls.map((url, index) => ({
        uid: `file-${Date.now()}-${index}`,
        name: url.substring(url.lastIndexOf('/') + 1),
        status: 'done',
        url: url,
        size: 0,
        type: 'image/png',
      }));
      setFileList(initialFileList);
      setImagePreviews(initialImageUrls);
    }
  }, [initialImageUrls]);

  const extractFileName = (url: string) => {
    const urlSegments = url.split('/');
    return urlSegments[urlSegments.length - 1];
  };

  const uploadImage = async (file: File): Promise<string> => {
    setUploading(true); // 업로드 시작
    const formData = new FormData();
    formData.append('image', file, file.name);

    try {
      const response = await axiosRequest.requestAxios<ResData<ImgUploadData>>('post', '/banner_img', formData);

      setUploading(false); // 업로드 완료
      return response.data.image_url;
    } catch (error) {
      setUploading(false); // 업로드 실패
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleImageRemove = (file: UploadFile) => {
    const newImagePreviews = imagePreviews.filter(url => url !== file.url);
    setImagePreviews(newImagePreviews);

    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);

    onImageUpload(newImagePreviews);
  };

  const handleImageRemoveByPreview = (url: string) => {
    const newImagePreviews = imagePreviews.filter(preview => preview !== url);
    setImagePreviews(newImagePreviews);

    const newFileList = fileList.filter(item => item.url !== url);
    setFileList(newFileList);

    onImageUpload(newImagePreviews);
  };

  const handleImageChange = async (file: RcFile, fileList: RcFile[]) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      const uploadResponse = await uploadImage(compressedFile);

      const newFile: UploadFile = {
        uid: `file-${Date.now()}`,
        name: file.name,
        status: 'done' as UploadFileStatus,
        url: uploadResponse,
        size: compressedFile.size,
        type: compressedFile.type,
      };

      setFileList(currentFileList => [...currentFileList, newFile]);
      setImagePreviews(currentPreviews => [...currentPreviews, uploadResponse]);

      onImageUpload([...imagePreviews, uploadResponse]);
    } catch (error) {
      console.error('Error during image compression or upload:', error);
    }
  };

  return (
    <Form.Item
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Upload.Dragger
        multiple
        beforeUpload={handleImageChange}
        fileList={fileList}
        onRemove={handleImageRemove}
        accept=".jpeg,.jpg,.png"
        style={{ width: '100%' }}
      >
        <p className="ant-upload-drag-icon">
          <UploadOutlined />
        </p>
        {uploadText}
      </Upload.Dragger>
      {uploading && (
        <p style={{ margin: '20px 0' }}>
          loading
          <LoadingOutlined />
        </p>
      )}
      {imagePreviews.map((imagePreview, index) => (
        <ImagePreview
          key={index}
          imagePreview={imagePreview}
          onRemove={() => handleImageRemoveByPreview(imagePreview)}
        />
      ))}
    </Form.Item>
  );
};

export default ImageUpload;
